import axios from 'axios'
import { get, isEmpty, isFunction } from 'lodash'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'
import safeJSONParse from '@emmacare/common/utils/safeJSONParse'

import {
  setActiveClinicianUsers,
  setClinicianUsers,
  setCreateCallLoading,
  setPatientsListWithDevice,
  setScheduledItems,
  setScheduledItemsLoading,
  setWaitingRoomCount
} from 'src/store/modules/actions/telehealth'
import { getClinicians as getCliniciansCommon } from 'src/store/modules/common'

import {
  API_BASE_URL,
  BATCH_DELETE,
  CALL_BACK_PATIENT,
  CALL_REASONS,
  CALL_REASONS_MUTATE,
  END_MEETING,
  END_TELEMEDICINE_MEETING,
  END_URGENT_CARE_MEETING,
  GET_ACTIVE_MEETING,
  GET_ATTENDEE_INFO,
  GET_CALLEE_BY_PATIENT,
  GET_WAITINGROOM_COUNT,
  JOIN_MEETING,
  MEETING_SEND_EMAIL,
  MEETING_SEND_SMS,
  OVERLAP_VALIDATION,
  PATIENT_LIST,
  PATIENT_REFERRAL_INFO_PATCH,
  QUICK_CLOSE_TASK,
  SCHEDULED_ITEM,
  SCHEDULED_ITEMS,
  STOP_MEETING_RECORDING
} from 'src/constants'
import { showSuccessModal, showWarningModal } from 'src/services/dialogs'

export const getScheduledItems =
  (query, isModalSchedule) => async (dispatch) => {
    if (!isModalSchedule) {
      dispatch(setScheduledItemsLoading(true))
    }

    try {
      const { items } = await httpClient.request({
        method: 'get',
        url: {
          pathname: SCHEDULED_ITEMS,
          query: {
            ...query,
            projection: 'list',
            'per-page': 1000,
            sort: query?.sort || 'startTime:asc'
          }
        }
      })

      if (!isModalSchedule) {
        dispatch(setScheduledItems(items))
      }

      return items
    } catch (err) {
      catchError(err)
    } finally {
      if (!isModalSchedule) {
        dispatch(setScheduledItemsLoading(false))
      }
    }
  }

export const getSchedulerList = (query) => async (dispatch) => {
  try {
    const { items } = await httpClient.request({
      method: 'get',
      url: {
        pathname: SCHEDULED_ITEMS,
        query: {
          ...query,
          projection: 'list',
          'per-page': 1000
        }
      }
    })

    return items
  } catch (err) {
    catchError(err)
  }
}

export const createConferenceCall =
  (payload, filters, callback) => async (dispatch) => {
    dispatch(setCreateCallLoading(true))
    try {
      const response = await httpClient.request({
        method: 'post',
        url: {
          pathname: SCHEDULED_ITEMS
        },
        payload
      })

      const [{ errorMessage }] = response

      if (errorMessage) {
        showWarningModal({
          content: errorMessage,
          onOk: () => window.close()
        })
      }

      if (isFunction(callback)) {
        callback(response[0])
      }

      return response[0]
    } catch (err) {
      catchError({
        ...err,
        message: handleServerError(err)
      })
    } finally {
      dispatch(setCreateCallLoading(false))
    }
  }

export const editConferenceCall =
  (payload, uuid, filters, callback) => async (dispatch) => {
    dispatch(setCreateCallLoading(true))
    try {
      const [meeting] = await httpClient.request({
        method: 'patch',
        url: {
          pathname: SCHEDULED_ITEM,
          params: { uuid }
        },
        payload
      })

      // dispatch(getWaitingRoomCount())

      const { errorMessage } = meeting

      if (filters) {
        // dispatch(getScheduledItems(filters))
      }

      if (isFunction(callback)) {
        callback(meeting)
      }

      if (errorMessage) {
        showWarningModal({
          content: errorMessage,
          onOk: () => window.close()
        })

        return false
      }

      return meeting
    } catch (err) {
      catchError({
        ...err,
        message: handleServerError(err)
      })
    } finally {
      dispatch(setCreateCallLoading(false))
    }
  }

export const deleteConferenceCall =
  (uuid = null, filters, query = {}) =>
  async (dispatch) => {
    try {
      const resp = await httpClient.request({
        method: 'delete',
        url: {
          pathname: SCHEDULED_ITEM,
          params: { uuid },
          query
        }
      })
      if (filters) {
        dispatch(getScheduledItems(filters))
      }

      // dispatch(getWaitingRoomCount())

      const errorMessage = get(resp, 'errorMessage')

      if (errorMessage) {
        showWarningModal({
          content: errorMessage,
          onOk: () => window.close()
        })
      }

      return true
    } catch (err) {
      if (get(err, 'errorMessage')) {
        showWarningModal({
          content: get(err, 'errorMessage'),
          onOk: () => window.close()
        })
      } else {
        catchError(err)
      }

      return false
    }
  }

export const deleteConferenceCallV2 = async (config = {}) => {
  const { params = {}, query = {} } = config

  try {
    await httpClient.request({
      method: 'delete',
      url: {
        pathname: SCHEDULED_ITEM,
        params,
        query
      }
    })

    return true
  } catch (err) {
    catchError(err)

    return false
  }
}

export const getCliniciansList =
  (query = {}) =>
  async (dispatch) => {
    try {
      const items = await getCliniciansCommon({
        query: {
          'per-page': 1000,
          sort: 'fullName:asc',
          ...query
        }
      })

      dispatch(setClinicianUsers(items))
    } catch (err) {
      catchError(err)
    }
  }

export const getActiveCliniciansList = () => async (dispatch) => {
  try {
    const items = await getCliniciansCommon({
      query: {
        'filter[enabled]': 1,
        'per-page': 1000
      }
    })

    dispatch(setActiveClinicianUsers(items))
  } catch (err) {
    catchError(err)
  }
}

export const startCall =
  (uuid, joined, context, route, payload, uc409Confirmation) =>
  async (_, getState) => {
    const userUuid = get(getState(), ['user', 'profile', 'uuid'])
    console.log('[startCall] Called with:', { uuid, route, payload, hasUc409Callback: !!uc409Confirmation })

    try {
      const meeting = await httpClient.request({
        method: 'post',
        url: {
          pathname: route || JOIN_MEETING,
          params: {
            uuid,
            userUuid
          }
        },
        payload
      })

      console.log('[startCall] Success:', meeting)

      if (get(meeting, 'errorMessage')) {
        showWarningModal({
          content: get(meeting, 'errorMessage')
        })
      }

      return meeting
    } catch (err) {
      console.log('[startCall] Error caught:', {
        status: err.status,
        message: err.message,
        hasUc409Callback: !!uc409Confirmation,
        willCallUc409: err.status === 409 && !!uc409Confirmation,
        willUseCatchError: !(err.status === 409 && uc409Confirmation)
      })

      if (err.status === 409 && uc409Confirmation) {
        console.log('[startCall] UC409: Calling uc409Confirmation callback')
        uc409Confirmation?.()
      } else {
        console.log('[startCall] Non-409 or missing callback: Using catchError')
        catchError(err, {
          onOk: async () => {
            if (context) {
              await context.setState({ closeWindowLeave: false })
            }

            window.close()
          }
        })
      }

      return {}
    }
  }

export const startAnonymouslyCall = (uuid, isUrgentCare) => async () => {
  try {
    let route = `${API_BASE_URL}/chat/chime/meeting/${uuid}/join-anonymously`

    if (isUrgentCare) {
      route = `${API_BASE_URL}/chat/urgent-care/${uuid}/join-anonymously`
    }

    const { data: meeting } = await axios.post(route)

    if (get(meeting, 'errorMessage')) {
      showWarningModal({
        content: get(meeting, 'errorMessage')
      })
    }

    return meeting
  } catch (err) {
    const { status = 500, data = '{}', headers = {} } = err.response || {}
    catchError(
      { headers, status, ...safeJSONParse(data) },
      {
        onOk: async () => {
          window.open('', '_self').close()
          window.history.go(-1)
        }
      }
    )

    return {}
  }
}

export const endCall = (uuid, payload, route) => async () => {
  try {
    const meeting = await httpClient.request({
      method: route ? 'post' : 'patch',
      url: {
        pathname: route ? END_URGENT_CARE_MEETING : END_MEETING,
        params: {
          uuid
        }
      },
      payload
    })

    return meeting
  } catch (err) {
    catchError(err)
  }
}

export const endTelemedicineMeeting = async (uuid) => {
  try {
    await httpClient.post({
      pathname: END_TELEMEDICINE_MEETING,
      params: { meetingUuid: uuid }
    })
  } catch (err) {
    catchError(err)
  }
}

export const getActiveMeeting = async (urgentCareCallUuid) => {
  try {
    return await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_ACTIVE_MEETING,
        params: { urgentCareCallUuid }
      }
    })
  } catch (err) {
    catchError(err)
    return []
  }
}

export const stopRecording = async (meetingUuid) => {
  try {
    await httpClient.patch({
      pathname: STOP_MEETING_RECORDING,
      params: { meeting: meetingUuid }
    })
  } catch (err) {
    catchError(err)
    throw err
  }
}

export const getWaitingRoomCount =
  (meetingUuid, shouldShowPopup) => async (dispatch) => {
    try {
      const { scheduled, waiting } = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_WAITINGROOM_COUNT
        }
      })

      dispatch(
        setWaitingRoomCount({
          scheduled: Number(scheduled),
          waiting: Number(waiting)
        })
      )
    } catch (err) {
      catchError(err)
    }
  }

export const getPatientsListWithDevice = (query) => async (dispatch) => {
  try {
    const patientsList = await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT_LIST,
        query
      }
    })
    // if (!patientsList?.items?.length) return

    dispatch(
      setPatientsListWithDevice(
        patientsList?.items?.map((e) => ({ ...e, name: e.fullName })) || []
      )
    )
  } catch (err) {
    catchError(err)
  }
}

export const handleServerError = (err) => {
  if (!isEmpty(get(err, 'errors', {}))) {
    return Object.keys(err.errors)
      .map((key) => get(err, ['errors', key, '0'], ''))
      .join(' ')
  } else {
    return get(err, 'message', '')
  }
}

export const checkClinicianOverlap = (payload) => async () => {
  const response = await httpClient.request({
    method: 'post',
    url: {
      pathname: OVERLAP_VALIDATION
    },
    payload
  })

  return response
}

export const sendSMS =
  (config = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: MEETING_SEND_SMS,
          params
        },
        payload
      })

      showSuccessModal({
        content: 'SMS has been successfully sent!',
        centered: true,
        keyboard: false
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const sendEmail =
  (config = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: MEETING_SEND_EMAIL,
          params
        },
        payload
      })

      showSuccessModal({
        content:
          'Email sent successfully. Please advise the recipient to check their spam or junk folder for an email from no-reply@emmacare.com if they do not receive it.',
        centered: true,
        keyboard: false
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const callBackPatient = (uuid) => async () => {
  try {
    await httpClient.request({
      method: 'post',
      url: {
        pathname: CALL_BACK_PATIENT,
        params: {
          uuid
        }
      }
    })

    return true
  } catch (err) {
    catchError(err)

    return false
  }
}

export const getCalleeByPatient = (uuid, query) => async () => {
  try {
    return await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_CALLEE_BY_PATIENT,
        params: {
          uuid
        },
        query
      }
    })
  } catch (err) {
    catchError(err)

    return false
  }
}

export const getAttendeeInfo = (awsMeetingId, awsAttendeeId) => async () => {
  const meeting = await httpClient.request({
    method: 'get',
    url: {
      pathname: GET_ATTENDEE_INFO,
      params: {
        awsMeetingId,
        awsAttendeeId
      }
    }
  })

  return meeting
}

export const getCallItem = async (uuid = '') => {
  try {
    const response = await httpClient.request({
      method: 'get',
      url: {
        pathname: SCHEDULED_ITEM,
        params: { uuid }
      }
    })

    return response
  } catch (err) {
    catchError({
      ...err,
      message: handleServerError(err)
    })

    return {}
  }
}

export const editSchedulerCallItem = (payload, uuid) => async (dispatch) => {
  try {
    const [meeting] = await httpClient.request({
      method: 'patch',
      url: {
        pathname: SCHEDULED_ITEM,
        params: { uuid }
      },
      payload
    })

    const { errorMessage } = meeting

    if (errorMessage) {
      showWarningModal({
        content: errorMessage,
        onOk: () => window.close()
      })

      return false
    }

    return meeting
  } catch (err) {
    catchError({
      ...err,
      message: handleServerError(err)
    })

    return false
  }
}

export const updatePatientReferralNote = async (config = {}) => {
  const { params = {}, payload = {} } = config

  try {
    const patientProfile = await httpClient.request({
      method: 'patch',
      url: {
        pathname: PATIENT_REFERRAL_INFO_PATCH,
        params
      },
      payload
    })

    return patientProfile
  } catch (err) {
    catchError(err)

    return false
  }
}

export const quickCloseAction = async (config = {}) => {
  const { params = {}, payload = {} } = config

  try {
    await httpClient.request({
      method: 'patch',
      url: {
        pathname: QUICK_CLOSE_TASK,
        params
      },
      payload
    })

    return true
  } catch (err) {
    catchError(err)

    return false
  }
}

export const updateCallReasons = async (config) => {
  try {
    return await httpClient.patch(
      {
        pathname: CALL_REASONS_MUTATE,
        params: config.params
      },
      config.payload
    )
  } catch (err) {
    catchError(err)
  }
}

export const batchDelete = async (uuids) => {
  try {
    return await httpClient.delete(
      {
        pathname: BATCH_DELETE
      },
      uuids
    )
  } catch (err) {
    catchError(err)
  }
}
